import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function Stars({ value, size = 14, onChange }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={onChange ? () => onChange(n) : undefined}
          disabled={!onChange}
          data-testid={onChange ? `star-input-${n}` : undefined}
          className={`transition-colors ${onChange ? "cursor-pointer" : "cursor-default"}`}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star
            size={size}
            strokeWidth={1.4}
            fill={n <= value ? "#C8A165" : "transparent"}
            className={n <= value ? "text-[#C8A165]" : "text-[#D4D4D4]"}
          />
        </button>
      ))}
    </div>
  );
}

export function ProductReviews({ productId }) {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ count: 0, average: 0 });
  const [form, setForm] = useState({ name: "", email: "", title: "", body: "", rating: 5 });
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!productId) return;
    api.get("/reviews", { params: { product_id: productId } })
      .then(({ data }) => setReviews(data || [])).catch(() => setReviews([]));
    api.get(`/products/${productId}/reviews/summary`)
      .then(({ data }) => setSummary(data)).catch(() => {});
  }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.body.trim()) {
      toast("Please fill in your name, email, and review.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/reviews", { ...form, product_id: productId });
      toast("Thank you. Your review has been submitted for approval.");
      setForm({ name: "", email: "", title: "", body: "", rating: 5 });
      setShowForm(false);
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not submit review.");
    }
    setBusy(false);
  };

  return (
    <section
      data-testid="product-reviews"
      className="mt-16 pt-12 border-t border-[#EFE7D8]"
    >
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="label-track text-[#C8A165] mb-2">Reviews</p>
          <h2 className="font-heading text-3xl text-[#0A0A0A]">Customer Impressions</h2>
          {summary.count > 0 && (
            <div className="mt-3 flex items-center gap-3">
              <Stars value={Math.round(summary.average)} size={16} />
              <span className="font-mono text-sm text-[#0A0A0A]">{summary.average.toFixed(1)}</span>
              <span className="text-sm text-[#737373]">
                {summary.count} review{summary.count !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="write-review-btn"
          className="border border-[#0A0A0A] text-[#0A0A0A] px-6 py-3 label-track hover:bg-[#0A0A0A] hover:text-white transition-colors"
        >
          {showForm ? "Cancel" : "Write a Review"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={submit}
          data-testid="review-form"
          className="mb-12 grid md:grid-cols-2 gap-x-6 gap-y-5 bg-[#FAFAF7] p-6 border border-[#EFE7D8]"
        >
          <div>
            <label className="label-track text-[#737373] text-[10px]">Your Name</label>
            <input
              data-testid="review-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="w-full bg-transparent border-b border-[#D4D4D4] focus:border-[#0A0A0A] outline-none py-2 mt-1"
            />
          </div>
          <div>
            <label className="label-track text-[#737373] text-[10px]">Email</label>
            <input
              data-testid="review-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="w-full bg-transparent border-b border-[#D4D4D4] focus:border-[#0A0A0A] outline-none py-2 mt-1"
            />
          </div>
          <div className="md:col-span-2">
            <label className="label-track text-[#737373] text-[10px]">Rating</label>
            <div className="mt-2">
              <Stars
                value={form.rating}
                size={22}
                onChange={(n) => setForm({ ...form, rating: n })}
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="label-track text-[#737373] text-[10px]">Title (optional)</label>
            <input
              data-testid="review-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-transparent border-b border-[#D4D4D4] focus:border-[#0A0A0A] outline-none py-2 mt-1"
              placeholder="Absolutely stunning"
            />
          </div>
          <div className="md:col-span-2">
            <label className="label-track text-[#737373] text-[10px]">Your Review</label>
            <textarea
              data-testid="review-body"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              required
              rows={4}
              className="w-full bg-white border border-[#D4D4D4] focus:border-[#0A0A0A] outline-none p-3 mt-2 text-sm"
              placeholder="Tell us about the piece and your experience..."
            />
          </div>
          <div className="md:col-span-2 flex items-center justify-between">
            <p className="text-xs text-[#737373]">
              Reviews are moderated before publishing.
            </p>
            <button
              type="submit"
              disabled={busy}
              data-testid="review-submit"
              className="bg-[#0A0A0A] text-white px-8 py-3 label-track hover:bg-[#C8A165] transition-colors disabled:opacity-50"
            >
              {busy ? "Submitting…" : "Submit Review"}
            </button>
          </div>
        </form>
      )}

      {reviews.length === 0 ? (
        <p
          className="text-[#737373] text-sm py-6"
          data-testid="reviews-empty"
        >
          Be the first to share your impression of this piece.
        </p>
      ) : (
        <div className="space-y-8 divide-y divide-[#EFE7D8]">
          {reviews.map((r) => (
            <div key={r.id} className="pt-8 first:pt-0" data-testid={`review-item-${r.id}`}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <Stars value={r.rating} size={13} />
                  {r.title && (
                    <span className="font-heading text-lg text-[#0A0A0A]">{r.title}</span>
                  )}
                </div>
                <span className="text-xs font-mono text-[#737373]">
                  {new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                </span>
              </div>
              <p className="text-[#0A0A0A] leading-relaxed">{r.body}</p>
              <p className="mt-3 label-track text-[#737373] text-[10px]">— {r.name}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
